import React, { useEffect, useMemo, useState } from "react";

import { Briefcase, Check, Loader2, Users, X } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../../Store/Hooks";
import type {
  CustomerTeamMember,
  StakeholderRelationship,
} from "../../../../Store/Reducers/AccountDetailsSlice";
import { previewMappingChange } from "../../../../Store/Reducers/TeamViewsSlice";
import { fetchRlSettingConfig } from "../../../../Store/Reducers/VendorAdminSlice";
import { toast } from "../../../../Utils/Helper/toast";
import {
  BuddyMatrixCell,
  BuddyMatrixSectionHeader,
  BuddyMatrixVendorRowLabel,
} from "../../../Relationship/account-relationships/buddy-matrix-ui";
import {
  CustomerTeamColumnHeader,
  CustomerTeamMeddGroupHeaderRow,
} from "../../../Relationship/account-relationships/customer-team-matrix-ui";
import MappingReviewModal from "../../../Relationship/account-relationships/MappingReviewModal";
import {
  buildCustomerTeamMeddGroupSpans,
  computeStakeholderMatrixStats,
  resolveContactMeddpiccMatrixMeta,
  resolveCustomerMeddRoleLabel,
  resolveCustomerMemberTitle,
  sortCustomerStakeholdersForDisplay,
} from "../../../Relationship/account-relationships/relationshipUtils";

import "../../../Relationship/account-relationships/relationship-prototype-skin.css";

export type VendorStakeholder = {
  id: string;
  name: string;
  /** Job title (position) — shown under name for account team in the matrix */
  title: string;
  roleLabel: string;
  source: "account" | "executive";
  sponsorType?: "primary" | "secondary" | null;
};

export type StakeholderSaveLink = {
  team_type: "account_team_member" | "executive_team_member";
  account_team_member?: string;
  executive_team_member?: string;
  customer_team_member: string;
  relationship_percentage: number | null;
  is_priority: boolean;
};

export type StakeholderTransferDecision = {
  relationship_id: string;
  successor_user_id?: string | null;
  leave_dormant?: boolean;
};

type MappingPreviewLink = {
  subject_type: "account_team_member" | "executive_team_member";
  subject_id: string;
  contact_id: string;
};

type RelationshipMatrixProps = {
  accountId: string;
  accountName?: string;
  vendorStakeholders: VendorStakeholder[];
  customerStakeholders: CustomerTeamMember[];
  relationships: StakeholderRelationship[];
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  onSave: (
    links: StakeholderSaveLink[],
    transferDecisions?: StakeholderTransferDecision[]
  ) => Promise<boolean>;
  onClose?: () => void;
};

const mapCustomerForMatrix = (member: CustomerTeamMember) => ({
  id: member.user_id,
  member_name: member.name,
  name: member.name,
  position: member.position,
  member_title: member.position || undefined,
  title: member.position || undefined,
  meddpicc_role: member.role,
  role: member.role,
});

const mapVendorForMatrix = (vendor: VendorStakeholder) => ({
  id: vendor.id,
  member_name: vendor.name,
  name: vendor.name,
  roleLabel: vendor.roleLabel,
  member_title: vendor.title,
  title: vendor.title,
  type: vendor.sponsorType || undefined,
  sponsorRole: vendor.sponsorType || undefined,
  __vendorType:
    vendor.source === "account" ? "account_team_member" : "executive",
});

const buildRelationshipLookup = (relationships: StakeholderRelationship[]) => {
  const keys = new Set<string>();
  const rlByKey = new Map<string, number | null>();
  const percentages = new Map<string, number | null>();

  relationships.forEach(item => {
    if (item.active === false) return;
    const customerId = item.customer_team_member;
    const vendorId =
      item.account_team_member ||
      item.executive_team_member ||
      (item as { vendor_stakeholder?: string }).vendor_stakeholder;

    if (!vendorId || !customerId) return;

    const key = `${vendorId}::${customerId}`;
    keys.add(key);
    percentages.set(key, item.relationship_percentage ?? null);
    const rl = item.latest_assessment?.rl;
    rlByKey.set(
      key,
      rl !== null && rl !== undefined && Number.isFinite(Number(rl))
        ? Number(rl)
        : null
    );
  });

  return { keys, rlByKey, percentages };
};

const RelationshipMatrix: React.FC<RelationshipMatrixProps> = ({
  accountId,
  accountName,
  vendorStakeholders,
  customerStakeholders,
  relationships,
  loading,
  error,
  saving,
  saveError,
  onSave,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const considerMeddpiccRole = useAppSelector(
    state => state.vendorAdmin.rlSettingValue?.consider_meddpicc_role === true
  );

  useEffect(() => {
    dispatch(fetchRlSettingConfig());
  }, [dispatch]);

  const {
    keys: relationshipKeys,
    rlByKey,
    percentages: relationshipPercentages,
  } = useMemo(() => buildRelationshipLookup(relationships), [relationships]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [draftKeys, setDraftKeys] = useState<Set<string>>(new Set());
  const [previewBusy, setPreviewBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewPreview, setReviewPreview] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [pendingLinks, setPendingLinks] = useState<StakeholderSaveLink[]>([]);
  const [pendingNewLinks, setPendingNewLinks] = useState<MappingPreviewLink[]>(
    []
  );

  useEffect(() => {
    if (!isEditMode) {
      setDraftKeys(new Set(relationshipKeys));
    }
  }, [relationshipKeys, isEditMode]);

  const activeKeys = isEditMode ? draftKeys : relationshipKeys;
  const busy = saving || previewBusy;

  const accountTeamVendors = useMemo(
    () => vendorStakeholders.filter(vendor => vendor.source === "account"),
    [vendorStakeholders]
  );

  const executiveTeamVendors = useMemo(
    () => vendorStakeholders.filter(vendor => vendor.source === "executive"),
    [vendorStakeholders]
  );

  const displayCustomers = useMemo(
    () =>
      sortCustomerStakeholdersForDisplay(
        customerStakeholders.map(mapCustomerForMatrix),
        { considerMeddpiccRole }
      ),
    [customerStakeholders, considerMeddpiccRole]
  );

  const customerTeamGroupSpans = useMemo(
    () =>
      buildCustomerTeamMeddGroupSpans(displayCustomers, {
        considerMeddpiccRole,
      }),
    [displayCustomers, considerMeddpiccRole]
  );

  const matrixStats = useMemo(
    () =>
      computeStakeholderMatrixStats(
        vendorStakeholders.map(v => v.id),
        displayCustomers.map(c => c.id),
        activeKeys,
        rlByKey
      ),
    [vendorStakeholders, displayCustomers, activeKeys, rlByKey]
  );

  const isDirty = useMemo(() => {
    if (draftKeys.size !== relationshipKeys.size) return true;
    return Array.from(draftKeys).some(key => !relationshipKeys.has(key));
  }, [draftKeys, relationshipKeys]);

  const isMatrixInteractive = isEditMode;
  const disableGrid =
    vendorStakeholders.length === 0 || displayCustomers.length === 0;

  const toggleDraftCell = (key: string) => {
    if (!isMatrixInteractive) return;
    setDraftKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCancelEdit = () => {
    setDraftKeys(new Set(relationshipKeys));
    setIsEditMode(false);
  };

  const buildDraftPayload = () => {
    const links: StakeholderSaveLink[] = [];
    const newLinks: MappingPreviewLink[] = [];

    vendorStakeholders.forEach(vendor => {
      displayCustomers.forEach(customer => {
        const key = `${vendor.id}::${customer.id}`;
        if (!draftKeys.has(key)) return;

        const subject_type =
          vendor.source === "account"
            ? ("account_team_member" as const)
            : ("executive_team_member" as const);

        newLinks.push({
          subject_type,
          subject_id: vendor.id,
          contact_id: customer.id,
        });

        if (vendor.source === "account") {
          links.push({
            team_type: "account_team_member",
            account_team_member: vendor.id,
            customer_team_member: customer.id,
            relationship_percentage: relationshipPercentages.get(key) ?? null,
            is_priority: true,
          });
        } else {
          links.push({
            team_type: "executive_team_member",
            executive_team_member: vendor.id,
            customer_team_member: customer.id,
            relationship_percentage: relationshipPercentages.get(key) ?? null,
            is_priority: true,
          });
        }
      });
    });

    return { links, newLinks };
  };

  const handleSaveEdit = async () => {
    if (!accountId || busy) return;
    setPreviewBusy(true);
    try {
      const { links, newLinks } = buildDraftPayload();
      const preview = await dispatch(
        previewMappingChange({ accountId, newLinks })
      ).unwrap();
      if (preview?.requires_review) {
        setPendingLinks(links);
        setPendingNewLinks(newLinks);
        setReviewPreview(preview);
        setReviewOpen(true);
        return;
      }
      const ok = await onSave(links);
      if (ok) handleClose();
    } catch (err: unknown) {
      const message =
        typeof err === "string"
          ? err
          : (err as { message?: string })?.message ||
            "Failed to preview mapping changes";
      toast.error(message);
    } finally {
      setPreviewBusy(false);
    }
  };

  const handleReviewConfirm = async (
    transferDecisions: StakeholderTransferDecision[]
  ) => {
    setPreviewBusy(true);
    try {
      const ok = await onSave(pendingLinks, transferDecisions);
      if (ok) {
        setReviewOpen(false);
        handleClose();
      }
    } finally {
      setPreviewBusy(false);
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    setDraftKeys(new Set(relationshipKeys));
    setReviewOpen(false);
    onClose?.();
  };

  const colSpan = displayCustomers.length + 1;

  return (
    <article
      id="modal-buddy"
      className={`rl-prototype-skin flex h-full min-h-0 flex-col${
        !isEditMode ? " coverage-matrix--readonly" : ""
      }`}
    >
      <header className="modal-hdr shrink-0">
        <div className="min-w-0 flex-1">
          <h2 className="modal-title">
            Stakeholder Mapping
            {accountName ? ` — ${accountName}` : ""}
          </h2>
          <p className="modal-sub">
            Map exec + account team members to customer contacts for
            relationship coverage
          </p>
        </div>
        {onClose ? (
          <button
            type="button"
            className="modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}
      </header>

      <div className="flex shrink-0 items-start gap-2 border-b border-gray-100 px-5 py-2 text-xs text-gray-600">
        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-600" />
        Check pairs where a relationship exists or can be developed. Checked
        pairs appear in the relationship matrix.
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-5 py-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
            Loading relationship matrix…
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
            {error}
          </div>
        ) : disableGrid ? (
          <div className="flex justify-center py-12 text-center text-sm text-gray-500">
            Add vendor and customer stakeholders to view the relationship
            matrix.
          </div>
        ) : (
          <>
            <table className="buddy-tbl">
              <thead>
                <CustomerTeamMeddGroupHeaderRow
                  groupSpans={customerTeamGroupSpans}
                />
                <tr>
                  <th className="rh" aria-label="Vendor stakeholders" />
                  {displayCustomers.map(customer => {
                    const name = customer.member_name || customer.name || "";
                    const memberTitle = resolveCustomerMemberTitle(customer);
                    const meddLabel = resolveCustomerMeddRoleLabel(customer);
                    const { meddGroup } =
                      resolveContactMeddpiccMatrixMeta(customer);
                    return (
                      <th
                        key={customer.id}
                        className={`c-col mcol-${meddGroup}`}
                        title={`${name}${memberTitle ? ` · ${memberTitle}` : ""}${
                          considerMeddpiccRole && meddLabel
                            ? ` · ${meddLabel}`
                            : ""
                        }`}
                      >
                        <CustomerTeamColumnHeader
                          member={customer}
                          showMeddpiccRole={considerMeddpiccRole}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {executiveTeamVendors.length > 0 ? (
                  <>
                    <BuddyMatrixSectionHeader
                      icon={Users}
                      label="Exec Team"
                      count={executiveTeamVendors.length}
                      colSpan={colSpan}
                      showPsLegend
                    />
                    {executiveTeamVendors.map(vendor => (
                      <tr key={`exec-${vendor.id}`}>
                        <td className="rh">
                          <BuddyMatrixVendorRowLabel
                            vendor={mapVendorForMatrix(vendor)}
                          />
                        </td>
                        {displayCustomers.map(customer => {
                          const key = `${vendor.id}::${customer.id}`;
                          const checked = activeKeys.has(key);
                          return (
                            <BuddyMatrixCell
                              key={`${vendor.id}-${customer.id}`}
                              checked={checked}
                              canEdit={isMatrixInteractive}
                              onToggle={() => toggleDraftCell(key)}
                              rl={
                                checked ? (rlByKey.get(key) ?? null) : undefined
                              }
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </>
                ) : null}
                {accountTeamVendors.length > 0 ? (
                  <>
                    <BuddyMatrixSectionHeader
                      icon={Briefcase}
                      label="Account Team"
                      count={accountTeamVendors.length}
                      colSpan={colSpan}
                    />
                    {accountTeamVendors.map(vendor => (
                      <tr key={`account-${vendor.id}`}>
                        <td className="rh">
                          <BuddyMatrixVendorRowLabel
                            vendor={mapVendorForMatrix(vendor)}
                          />
                        </td>
                        {displayCustomers.map(customer => {
                          const key = `${vendor.id}::${customer.id}`;
                          const checked = activeKeys.has(key);
                          return (
                            <BuddyMatrixCell
                              key={`${vendor.id}-${customer.id}`}
                              checked={checked}
                              canEdit={isMatrixInteractive}
                              onToggle={() => toggleDraftCell(key)}
                              rl={
                                checked ? (rlByKey.get(key) ?? null) : undefined
                              }
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </>
                ) : null}
              </tbody>
            </table>
          </>
        )}
      </div>

      {!disableGrid && !loading && !error ? (
        <footer className="buddy-footer shrink-0 border-t border-gray-200 px-5 py-3">
          <div className="buddy-legend">
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#22c55e" }}
              />
              Strong (RL 4–5)
            </span>
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#eab308" }}
              />
              Moderate (RL 2–3)
            </span>
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#ef4444" }}
              />
              Weak (RL 0–1)
            </span>
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#94a3b8" }}
              />
              Unassessed (RL ?)
            </span>
          </div>
          <div className="buddy-stats text-sm text-gray-600">
            {matrixStats.strong} strong · {matrixStats.moderate} moderate ·{" "}
            {matrixStats.weak} weak · {matrixStats.unassessed} unassessed ·{" "}
            {matrixStats.notMapped} not mapped
          </div>
          <div className="buddy-actions">
            {isEditMode ? (
              <>
                <button
                  type="button"
                  className="btn btn-sec"
                  onClick={handleCancelEdit}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-pri"
                  onClick={handleSaveEdit}
                  disabled={busy || !isDirty}
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save Stakeholder Mapping
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-pri"
                onClick={() => {
                  setDraftKeys(new Set(relationshipKeys));
                  setIsEditMode(true);
                }}
                disabled={busy}
              >
                Edit Stakeholder Mapping
              </button>
            )}
          </div>
        </footer>
      ) : null}

      <MappingReviewModal
        open={reviewOpen}
        preview={reviewPreview}
        newLinkCandidates={vendorStakeholders}
        vendorLookup={vendorStakeholders}
        remainingCoverageLinks={pendingNewLinks}
        nested
        onClose={() => setReviewOpen(false)}
        onConfirm={handleReviewConfirm}
      />
    </article>
  );
};

export default RelationshipMatrix;
